import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Stack,
  alpha,
  useTheme,
  Avatar,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
} from '@mui/material';
import {
  Save,
  Lock,
  Security,
  CheckCircle,
  Cancel,
  Refresh,
  CheckBox,
  CheckBoxOutlineBlank,
  Info,
  Warning,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getRoles } from '../redux/actions/userRoleActions';
import { getRolePermissions, updateRolePermissions, getAvailableModules } from '../redux/actions/userRoleActions';

const RolePermission = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { roles, loading: rolesLoading } = useSelector((state) => state.roles || { roles: [], loading: false });
  const { permissions, loading: permissionsLoading } = useSelector((state) => state.rolePermissions || { permissions: [], loading: false });
  const { modules } = useSelector((state) => state.availableModules || { modules: { modules: [] } });

  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [permissionMap, setPermissionMap] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadRoles();
    loadAvailableModules();
  }, []);

  useEffect(() => {
    if (selectedRoleId) {
      loadPermissions(selectedRoleId);
      setHasChanges(false);
    }
  }, [selectedRoleId]);

  const loadRoles = async () => {
    await dispatch(getRoles());
  };

  const loadAvailableModules = async () => {
    await dispatch(getAvailableModules());
  };

  const loadPermissions = async (roleId) => {
    const result = await dispatch(getRolePermissions(roleId));
    if (result.success) {
      const permMap = {};
      result.data.forEach(perm => {
        const key = `${perm.module}_${perm.permission}`;
        permMap[key] = perm.is_granted;
      });
      setPermissionMap(permMap);
      setHasChanges(false);
    }
  };

  const handleRoleChange = (roleId) => {
    setSelectedRoleId(roleId);
    setPermissionMap({});
    setHasChanges(false);
  };

  const handlePermissionChange = (module, permission, granted) => {
    const key = `${module}_${permission}`;
    setPermissionMap({
      ...permissionMap,
      [key]: granted
    });
    setHasChanges(true);
  };

  const handleSelectAllInModule = (module) => {
    const newMap = { ...permissionMap };
    module.permissions.forEach(permission => {
      const key = `${module.name}_${permission}`;
      newMap[key] = true;
    });
    setPermissionMap(newMap);
    setHasChanges(true);
  };

  const handleDeselectAllInModule = (module) => {
    const newMap = { ...permissionMap };
    module.permissions.forEach(permission => {
      const key = `${module.name}_${permission}`;
      newMap[key] = false;
    });
    setPermissionMap(newMap);
    setHasChanges(true);
  };

  const handleSelectAll = () => {
    const newMap = { ...permissionMap };
    modules.modules?.forEach(module => {
      module.permissions.forEach(permission => {
        const key = `${module.name}_${permission}`;
        newMap[key] = true;
      });
    });
    setPermissionMap(newMap);
    setHasChanges(true);
  };

  const handleDeselectAll = () => {
    const newMap = { ...permissionMap };
    modules.modules?.forEach(module => {
      module.permissions.forEach(permission => {
        const key = `${module.name}_${permission}`;
        newMap[key] = false;
      });
    });
    setPermissionMap(newMap);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedRoleId) {
      setSnackbar({
        open: true,
        message: 'Please select a role first',
        severity: 'warning'
      });
      return;
    }

    setSaving(true);
    
    // Build permissions array from permissionMap
    const permissionsArray = [];
    modules.modules?.forEach(module => {
      module.permissions.forEach(permission => {
        const key = `${module.name}_${permission}`;
        permissionsArray.push({
          module: module.name,
          permission: permission,
          is_granted: permissionMap[key] === true
        });
      });
    });

    const result = await dispatch(updateRolePermissions(selectedRoleId, permissionsArray));
    
    if (result.success) {
      setSnackbar({
        open: true,
        message: 'Permissions updated successfully',
        severity: 'success'
      });
      loadPermissions(selectedRoleId);
      setHasChanges(false);
    } else {
      setSnackbar({
        open: true,
        message: result.message || 'Failed to update permissions',
        severity: 'error'
      });
    }
    
    setSaving(false);
  };

  const selectedRole = roles.find(r => r.id === parseInt(selectedRoleId));

  const getModuleStats = (module) => {
    const total = module.permissions.length;
    const granted = module.permissions.filter(permission => {
      const key = `${module.name}_${permission}`;
      return permissionMap[key] === true;
    }).length;
    return { total, granted };
  };

  const getOverallStats = () => {
    if (!modules.modules) return { total: 0, granted: 0 };
    let total = 0;
    let granted = 0;
    modules.modules.forEach(module => {
      module.permissions.forEach(permission => {
        total++;
        const key = `${module.name}_${permission}`;
        if (permissionMap[key] === true) granted++;
      });
    });
    return { total, granted };
  };

  const overallStats = getOverallStats();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Role Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure access permissions for each role across all system modules
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={() => {
                if (selectedRoleId) loadPermissions(selectedRoleId);
                loadRoles();
                loadAvailableModules();
              }} 
              disabled={saving || permissionsLoading}
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={!selectedRoleId || saving || !hasChanges}
            sx={{ 
              borderRadius: 2,
              px: 3,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </Button>
        </Box>
      </Box>

      {/* Role Selection Card */}
      <Card sx={{ boxShadow: 2 }}>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Select Role</InputLabel>
            <Select
              value={selectedRoleId}
              onChange={(e) => handleRoleChange(e.target.value)}
              label="Select Role"
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                }
              }}
              startAdornment={
                <InputAdornment position="start" sx={{ ml: 1 }}>
                  <Security fontSize="small" color="action" />
                </InputAdornment>
              }
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Security fontSize="small" />
                    {role.name}
                    {role.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        - {role.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {selectedRoleId && selectedRole && (
        <>
          {/* Role Info Card */}
          <Card sx={{ 
            boxShadow: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    width: 56, 
                    height: 56, 
                    bgcolor: 'primary.main',
                  }}>
                    <Security sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {selectedRole.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRole.description || 'No description provided'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {overallStats.granted}/{overallStats.total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Permissions Granted
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(overallStats.granted / overallStats.total) * 100} 
                    sx={{ 
                      mt: 1, 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          {modules.modules && modules.modules.length > 0 && (
            <Card sx={{ boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lock fontSize="small" />
                    Quick Actions
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<CheckBox />}
                      onClick={handleSelectAll}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      startIcon={<CheckBoxOutlineBlank />}
                      onClick={handleDeselectAll}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      Deselect All
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Permissions Grid */}
          {permissionsLoading ? (
            <Card sx={{ boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                  <CircularProgress size={48} />
                </Box>
              </CardContent>
            </Card>
          ) : modules.modules && modules.modules.length > 0 ? (
            <Grid container spacing={2}>
              {modules.modules.map((module) => {
                const stats = getModuleStats(module);
                const allSelected = stats.granted === stats.total && stats.total > 0;
                const someSelected = stats.granted > 0 && stats.granted < stats.total;
                
                return (
                  <Grid item xs={12} md={6} lg={4} key={module.name}>
                    <Card 
                      variant="outlined"
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-2px)'
                        },
                        border: allSelected ? `2px solid ${theme.palette.success.main}` : 
                                someSelected ? `2px solid ${theme.palette.warning.main}` : 
                                `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Module Header */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          mb: 2,
                          pb: 2,
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                        }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                              {module.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {module.description || `${module.name} module`}
                            </Typography>
                          </Box>
                          <Badge 
                            badgeContent={stats.granted} 
                            color={allSelected ? 'success' : someSelected ? 'warning' : 'default'}
                            max={stats.total}
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.7rem',
                                height: 20,
                                minWidth: 20,
                                padding: '0 4px'
                              }
                            }}
                          >
                            <Lock color={allSelected ? 'success' : someSelected ? 'warning' : 'action'} />
                          </Badge>
                        </Box>

                        {/* Module Actions */}
                        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            startIcon={<CheckBox />}
                            onClick={() => handleSelectAllInModule(module)}
                            variant="outlined"
                            disabled={allSelected}
                            sx={{ 
                              borderRadius: 2,
                              flex: 1,
                              fontSize: '0.75rem'
                            }}
                          >
                            All
                          </Button>
                          <Button
                            size="small"
                            startIcon={<CheckBoxOutlineBlank />}
                            onClick={() => handleDeselectAllInModule(module)}
                            variant="outlined"
                            disabled={stats.granted === 0}
                            sx={{ 
                              borderRadius: 2,
                              flex: 1,
                              fontSize: '0.75rem'
                            }}
                          >
                            None
                          </Button>
                        </Box>

                        {/* Permissions List */}
                        <Box sx={{ 
                          flex: 1,
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: 1,
                          maxHeight: 300,
                          overflowY: 'auto',
                          pr: 1,
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: alpha(theme.palette.divider, 0.1),
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: alpha(theme.palette.primary.main, 0.3),
                            borderRadius: '3px',
                            '&:hover': {
                              background: alpha(theme.palette.primary.main, 0.5),
                            },
                          },
                        }}>
                          {module.permissions.map((permission) => {
                            const key = `${module.name}_${permission}`;
                            const isGranted = permissionMap[key] === true;
                            
                            return (
                              <Paper
                                key={permission}
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  bgcolor: isGranted 
                                    ? alpha(theme.palette.success.main, 0.05)
                                    : 'transparent',
                                  borderColor: isGranted 
                                    ? alpha(theme.palette.success.main, 0.3)
                                    : alpha(theme.palette.divider, 0.3),
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    borderColor: theme.palette.primary.main,
                                  }
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isGranted}
                                      onChange={(e) => handlePermissionChange(module.name, permission, e.target.checked)}
                                      color="primary"
                                      sx={{
                                        '&.Mui-checked': {
                                          color: theme.palette.success.main
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          textTransform: 'capitalize',
                                          fontWeight: isGranted ? 'medium' : 'normal',
                                          color: isGranted ? 'text.primary' : 'text.secondary'
                                        }}
                                      >
                                        {permission.replace(/_/g, ' ')}
                                      </Typography>
                                      {isGranted && (
                                        <CheckCircle 
                                          fontSize="small" 
                                          color="success" 
                                          sx={{ ml: 'auto' }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  sx={{ 
                                    m: 0,
                                    width: '100%',
                                    '& .MuiFormControlLabel-label': {
                                      width: '100%'
                                    }
                                  }}
                                />
                              </Paper>
                            );
                          })}
                        </Box>

                        {/* Module Footer */}
                        <Box sx={{ 
                          mt: 2, 
                          pt: 2,
                          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                        }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={(stats.granted / stats.total) * 100} 
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {stats.granted} of {stats.total} permissions granted
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Card sx={{ boxShadow: 2 }}>
              <CardContent>
                <Alert 
                  severity="info" 
                  icon={<Info />}
                  sx={{ borderRadius: 2 }}
                >
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    No modules available
                  </Typography>
                  <Typography variant="caption">
                    Please configure system modules first to manage permissions.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!selectedRoleId && (
        <Card sx={{ boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 6,
              textAlign: 'center'
            }}>
              <Avatar sx={{ 
                width: 80, 
                height: 80, 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <Security sx={{ fontSize: 48, color: 'primary.main' }} />
              </Avatar>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a Role
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a role from the dropdown above to configure its permissions
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {hasChanges && selectedRoleId && (
        <Alert 
          severity="warning" 
          icon={<Warning />}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                if (selectedRoleId) loadPermissions(selectedRoleId);
              }}
            >
              Reset
            </Button>
          }
          sx={{ boxShadow: 2 }}
        >
          You have unsaved changes. Don't forget to save your permissions.
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolePermission;
